import { workshopAnswerOptions } from "@/lib/workshop-answers";
import { Star } from "lucide-react";
import { WorkshopVoters } from "./workshop-voters";
import type { WorkshopQuestion } from "@/types/workshop";
import styles from "./workshops.module.css";

export function WorkshopOptions({
  question,
  selected,
  selectedIndices = [],
  selectedResponseId,
  onSelect,
  disabled = false,
  responses,
}: {
  question: WorkshopQuestion;
  selected?: number | "other" | null;
  selectedIndices?: number[];
  selectedResponseId?: string;
  onSelect?: (option: number | "other") => void;
  disabled?: boolean;
  responses?: {
    id: string;
    name: string;
    kind?: "answer" | "question" | "concern" | "skip";
    optionIndex: number | null;
    optionIndices?: number[];
    text: string;
  }[];
}) {
  const options = question.options
    .map((label, index) => ({ label, index }))
    .sort(
      (a, b) =>
        Number(b.index === question.recommendedOption) -
        Number(a.index === question.recommendedOption),
    );
  const customResponses =
    responses?.filter(
      (response) =>
        response.kind !== "skip" && !workshopAnswerOptions(response).length,
    ) ?? [];
  const choices: {
    label: string;
    index: number | null;
    responseId?: string;
  }[] = [...options];
  if (customResponses.length) {
    choices.push(
      ...customResponses.map((response) => ({
        label: response.text,
        index: null,
        responseId: response.id,
      })),
    );
  }
  if (
    question.type !== "text" &&
    (onSelect || (!customResponses.length && responses))
  ) {
    choices.push({ label: "Something else", index: null });
  }
  return (
    <fieldset
      className={styles.choices}
      data-written={question.type === "text"}
    >
      <legend className="sr-only">
        {onSelect ? "Choose an answer" : "Answer options"}
      </legend>
      {choices.map(({ label, index, responseId }) => {
        const select = responseId ? undefined : onSelect;
        const Choice = select ? "button" : "div";
        const option = index ?? "other";
        const votes = responses?.filter((response) =>
          responseId
            ? response.id === responseId
            : index !== null && workshopAnswerOptions(response).includes(index),
        );
        return (
          <Choice
            key={responseId ?? option}
            className={styles.choice}
            data-selected={
              responseId
                ? selectedResponseId === responseId
                : selected === option ||
                  (index !== null && selectedIndices.includes(index))
            }
            type={select ? "button" : undefined}
            disabled={select ? disabled : undefined}
            aria-pressed={
              select
                ? selected === option ||
                  (index !== null && selectedIndices.includes(index))
                : undefined
            }
            onClick={select ? () => select(option) : undefined}
          >
            <span>
              <strong
                className={
                  responseId
                    ? question.type === "text"
                      ? styles.writtenAnswer
                      : styles.customAnswer
                    : undefined
                }
                title={responseId ? label : undefined}
              >
                {label}
              </strong>
              {index !== null && index === question.recommendedOption && (
                <>
                  <span
                    className={styles.recommended}
                    role="img"
                    aria-label="Recommended answer"
                    title={
                      question.recommendation
                        ? `Recommended answer\n${question.recommendation}`
                        : "Recommended answer"
                    }
                  >
                    <Star size={13} fill="currentColor" aria-hidden="true" />
                  </span>
                  {!question.optionDescriptions && question.recommendation && (
                    <small>{question.recommendation}</small>
                  )}
                </>
              )}
              {index !== null && question.optionDescriptions && (
                <small>{question.optionDescriptions[index]}</small>
              )}
              {index === null && !responseId && (
                <small>Share a different answer.</small>
              )}
            </span>
            <span className={styles.choiceMeta}>
              <WorkshopVoters voters={votes ?? []} />
              <span
                className={styles.voteCount}
                style={{ visibility: votes ? "visible" : "hidden" }}
              >
                {votes?.length ?? 0} {votes?.length === 1 ? "vote" : "votes"}
              </span>
            </span>
          </Choice>
        );
      })}
      {responses?.some((response) => response.kind === "skip") && (
        <div className={styles.choice}>
          <strong>Skipped</strong>
          <WorkshopVoters
            voters={responses.filter((response) => response.kind === "skip")}
          />
        </div>
      )}
    </fieldset>
  );
}
